import { Link } from "gatsby";
import React from "react";
import styled from "styled-components";
import Button from "../ui/buttons/Button";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import PortableTextComponent from "../ui/PortableTextComponent";
import { PortableText } from "@portabletext/react";

// Import Swiper Styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

const StyledClientTestimonials = styled.section`
  display: flex;
  padding: var(--global-padding);
  flex-direction: column;
  align-items: center;
  gap: 64px;

  .section-heading-container {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 24px;
    width: 100%;

    .section-heading-text {
      display: flex;
      flex-direction: column;
      gap: 24px;
      .headings {
        display: flex;
        flex-direction: column;
        align-items: start;
        gap: 12px;

        .heading {
          color: var(--black);
          font-style: var(--font-family-heading);
          font-size: 36px;
          line-height: 43.2px;
        }
        .subheading {
          color: var(--red);
          font-style: var(--font-family-base-italic);
          font-style: italic;
          font-weight: 400;
          font-size: 16px;
          line-height: 20.8px;
        }
      }
      .text-content {
        font-size: 18px;
        line-height: 27px;
      }
    }
    .button-container {
      display: flex;
      align-items: end;
    }
  }

  .section-content {
    width: 100%;
    /* padding-bottom: 200px; */
    .swiper {
      position: relative;
      width: 100%;
      height: fit-content;
      display: flex;
      flex-direction: column;
      z-index: 0;
      padding: 0 24px;
      padding-bottom: calc(48px + 28px);
      @media (max-width: 768px) {
        padding: 0 12px;
        padding-bottom: calc(32px + 28px);
      }
      /* Hide extra pagination padding if all slides fit screen - no pagination will show  */
      &:has(.swiper-button-next.swiper-button-lock) {
        padding-bottom: 0;
      }
      .swiper-slide {
        margin-bottom: 4px;
        /* background-color: cyan; */
        /* width: 500px; */
        /* 0px 4px 8px -2px rgba(0, 0, 0, 0.1); */
        .card {
          box-shadow: 0px 2px 4px -2px rgba(0, 0, 0, 0.06),
            0px 4px 8px -2px rgba(0, 0, 0, 0.1);
          display: flex;
          flex-direction: column;
          gap: 24px;
          padding: 32px;
          .review-content > p {
            font-style: italic;
            font-size: 18px;
            line-height: 27px;
            color: var(--neutral-75);
          }
          .review-author {
            font-size: 18px;
            font-style: italic;
            color: var(--red);
          }
        }
      }
      .swiper-button-prev,
      .swiper-button-next {
        background-color: var(--red);
        width: 48px;
        padding: 9px 0px;
        &:after {
          color: var(--white);
          font-size: 20px;
          font-style: normal;
          font-weight: 900;
          line-height: 150%;
        }
        @media (max-width: 768px) {
          top: unset;
          bottom: 0;
          z-index: 2;
        }
        &:hover {
          background-color: var(--red-110);
        }
        &.swiper-button-disabled {
          display: none;
        }
      }
      .swiper-button-prev,
      .swiper-rtl .swiper-button-next {
        left: 0;
      }
      .swiper-button-next,
      .swiper-rtl .swiper-button-prev {
        right: 0px;
      }
      .swiper-pagination {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        margin: 0;
        z-index: 1;
        .swiper-pagination-bullet {
          width: 8px;
          height: 8px;
          border-radius: 0;
          opacity: 1;
          background-color: var(--neutral-50);
          &.swiper-pagination-bullet-active {
            background-color: var(--grey);
          }
        }
      }
    }
  }

  //MOBILE
  @media (max-width: 768px) {
    padding: var(--global-padding-mobile);
    gap: 40px;
    .section-heading-container {
      flex-direction: column;
      .section-heading-text {
        .headings {
          .heading {
            font-size: 24px;
            line-height: 28.4px;
          }
        }
        .text-content {
          font-size: 16px;
          line-height: 24px;
        }
      }
      .button-container {
      }
    }
    .section-content {
      .swiper-slide {
        /* max-width: 90%; */
        /* padding-inline: 10px; */
        .card {
          .review-content {
            font-size: 16px;
            line-height: 24px;
          }
          .review-author {
            font-size: 16px;
          }
        }
      }
    }
  }
`;

const ClientTestimonials = ({
  heading = "WHAT OUR CLIENTS SAY",
  subheading = "Testimonial",
  content,
  // link = "/",
  // linkText = "View All",
  link,
  linkText,
  testimonials = [],
  addToAnchor,
}) => {
  //another card for nonProduct testimonials?
  const testimonialCards = testimonials.map((testimonial, index) => (
    <SwiperSlide key={`ct-s-c-${index}`} className="swiper-slide">
      <div className="card">
        <div className="review-content">
          <PortableText
            value={testimonial._rawMainContent}
            components={PortableTextComponent}
          />
        </div>
        <p className="review-author">{testimonial.author}</p>
      </div>
    </SwiperSlide>
  ));

  return (
    <StyledClientTestimonials
      id={addToAnchor ? `anchor_testimonials` : ""}
      className={`${addToAnchor && "anchor-link"}`}
    >
      <div className="section-heading-container">
        <div className="section-heading-text">
          <div className="headings">
            <p className="subheading">{subheading}</p>
            <h2 className="heading">{heading}</h2>
          </div>
          {!!content && <p className="text-content">{content}</p>}
        </div>

        {!!link && !!linkText && (
          <div className="button-container">
            <Link to={link}>
              <Button
                size="md"
                btnStyle="outline"
                className=""
                value={linkText}
                clickHandler={false}
                isDisabled={false}
              />
            </Link>
          </div>
        )}
      </div>

      {testimonialCards.length > 0 && (
        <div className="section-content">
          <Swiper
            slidesPerView={1}
            spaceBetween={36}
            pagination={{
              clickable: true,
            }}
            navigation={true}
            modules={[Pagination, Navigation]}
            breakpoints={{
              768: {
                slidesPerView: 3,
              },
            }}
          >
            {testimonialCards}
          </Swiper>
        </div>
      )}
    </StyledClientTestimonials>
  );
};

export default ClientTestimonials;
